import type { ReactNode } from "react";
import { ChevronDown } from "./icons";
import { cn } from "./cn";
import { controlClass } from "./primitives";

/**
 * A labelled native `<select>`, matching `Field` exactly so a form's controls line up.
 *
 * **Native, not Radix Select.** The Radix version carries "use client", so a single dropdown would
 * drag this whole package across the client boundary (R5) and turn a `<form>` + Server Action page
 * into a client tree. A native select also gets the platform's own behaviour free: the OS picker on
 * a phone, type-ahead, and correct behaviour with JavaScript disabled (constitution VII).
 *
 * The chevron is drawn over the control with `appearance-none`, because the browser's default
 * arrow cannot be styled and looked foreign next to the themed inputs beside it.
 */
export function SelectField({
  label,
  name,
  error,
  hint,
  id: idOverride,
  className,
  wrapperClassName,
  children,
  required,
  disabledReason,
  ...select
}: {
  label: string;
  name: string;
  error?: string;
  hint?: ReactNode;
  id?: string;
  /** Classes for the label+control+hint column — e.g. `col-span-full` inside a `FormGroup` grid. */
  wrapperClassName?: string;
  /**
   * Why this select is disabled — e.g. "Select a category first." Shown in place of `hint` while
   * `select.disabled` is true, so a dependent dropdown never sits there mute (constitution VI's
   * "never fail silently" applied to a disabled control, not just an error).
   */
  disabledReason?: ReactNode;
} & React.ComponentProps<"select">) {
  // Not useId(): that is a client hook, and this component must stay server-renderable. `name` is
  // unique within a form, which is the same guarantee useId would buy here.
  const id = idOverride ?? `field-${name}`;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const effectiveHint = select.disabled && disabledReason ? disabledReason : hint;

  return (
    <div className={cn("flex flex-col gap-1.5", wrapperClassName)}>
      <label htmlFor={id} className="text-sm font-medium">
        {label}
        {required ? (
          <span aria-hidden="true" className="text-red">
            {" "}
            *
          </span>
        ) : null}
      </label>

      <div className="relative">
        <select
          id={id}
          name={name}
          data-slot="select"
          required={required}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={
            [error ? errorId : null, effectiveHint ? hintId : null].filter(Boolean).join(" ") || undefined
          }
          // `bg-none` drops @tailwindcss/forms' own chevron image; the drawn one below replaces it.
          className={cn(controlClass, "appearance-none bg-none pr-9 [&>option]:bg-gray-0 [&>option]:text-foreground", className)}
          {...select}
        >
          {children}
        </select>
        <ChevronDown
          aria-hidden
          className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2"
        />
      </div>

      {effectiveHint ? (
        <p id={hintId} className="text-muted-foreground text-xs">
          {effectiveHint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} role="alert" className="mt-0.5 text-[13px] text-red">
          {error}
        </p>
      ) : null}
    </div>
  );
}
